import { ListPlus, Star, StarOff } from "lucide-react";
import { toast } from "sonner";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import logger from "@/lib/logger";
import type { DropdownItem } from "@/main/react/buttons/cta/CtaButtonDropdown";
import type { Localizer } from "@/main/react/Types";
import { type GroupList, isFavoriteGroup } from "./groupListsModel";
import { setGroupFavorite } from "./groupListsQuery";

/**
 * The two menu items SV-144 adds wherever a participant meets a group (A14 as amended by Q2 = B:
 * member OR leader, not banned): the group page and card overflow (`GroupButtons`) and the chat header
 * menu (`GroupConversationMenu`). One builder, so the two menus cannot drift apart.
 *
 * `Add to favorites` / `Remove from favorites` toggles the built-in list in one call and says so in a
 * toast. `Add to lists…` opens the lazy sheet (rule 26, chunk `group-add-to-lists-sheet`).
 */
export function buildGroupListMenuItems({
    groupPublicId,
    lists,
    localizer,
    onChanged,
}: {
    groupPublicId: string;
    lists: readonly GroupList[];
    localizer: Localizer;
    /** Called after the server accepted a change, so a host can re-read what it derives from lists. */
    onChanged?: () => void;
}): DropdownItem[] {
    const favorite = isFavoriteGroup(lists, groupPublicId);
    return [
        {
            id: favorite ? "group-favorite-remove" : "group-favorite-add",
            icon: favorite ? StarOff : Star,
            text: localizer(favorite ? "groups.favorite.remove" : "groups.favorite.add"),
            requiresLogin: false,
            onClick: async () => {
                try {
                    await setGroupFavorite(groupPublicId, !favorite);
                    toast.success(localizer(favorite ? "groups.favorite.removed" : "groups.favorite.added"));
                    onChanged?.();
                } catch (error) {
                    logger.warn("Failed to change a group's favorite state", error);
                    toast.error(localizer("shared.action.failed"));
                }
            },
        },
        {
            id: "group-add-to-lists",
            icon: ListPlus,
            text: localizer("groups.lists.add_to"),
            requiresLogin: false,
            onClick: () => {
                openAddToListsSheet({ groupPublicId, localizer, onChanged });
            },
        },
    ];
}

export function openAddToListsSheet(options: { groupPublicId: string; localizer: Localizer; onChanged?: () => void }): void {
    void importChunkWithRetryAndReload(
        () => import("./AddToListsSheet"),
        "group-add-to-lists-sheet",
    ).then((module) => {
        module.default(options);
    });
}
